/**
 * useRequireAuth: gate an action behind a signed-in account.
 *
 * Guests keep full read/draw access; when they hit an account-only action
 * (submit, share, fork), call `requireAuth(action, context)` — it runs the
 * action immediately for signed-in users, or opens the sign-in modal with
 * context copy and runs the action right after a successful sign-in.
 *
 * Render the returned `signInModal` element once near the top of the page.
 */
import { useCallback, useState } from 'react';
import { SignInModal } from '../components/SignInModal';
import { getAccessToken } from '../lib/authToken';
import { selectIsAuthenticated, useAuthStore } from '../stores/auth';

interface PendingAction {
  action: () => void;
  context: string;
}

export function useRequireAuth(): {
  requireAuth: (action: () => void, context: string) => void;
  signInModal: JSX.Element;
} {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const [pending, setPending] = useState<PendingAction | null>(null);

  const requireAuth = useCallback(
    (action: () => void, context: string) => {
      if (isAuthenticated && getAccessToken()) {
        action();
        return;
      }
      setPending({ action, context });
    },
    [isAuthenticated],
  );

  const handleSuccess = useCallback(() => {
    const run = pending?.action;
    setPending(null);
    // Run after the modal closes so the UI settles first.
    if (run) window.setTimeout(run, 0);
  }, [pending]);

  const signInModal = (
    <SignInModal
      open={pending !== null}
      context={pending?.context ?? ''}
      onClose={() => setPending(null)}
      onSuccess={handleSuccess}
    />
  );

  return { requireAuth, signInModal };
}
